/**
 * Incident (alarm-event) hooks — list + acknowledge/resolve, plus the active/
 * triggered counters used by the dashboard. Alarm-events are tenant-scoped.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useAuth } from '@/auth/AuthProvider';
import {
  healthcheckApi,
  hcKeys,
  unwrap,
  unwrapList,
  type AlarmEvent,
  type AlarmEventStatus,
  type AlarmEventCount,
  type ListPagination,
} from './_shared';

export interface IncidentListFilters {
  status?: AlarmEventStatus;
  alarm_id?: string;
  page?: number;
  page_size?: number;
}

export interface IncidentListResult {
  items: AlarmEvent[];
  pagination?: ListPagination;
}

export function useAlarmEvents(filters: IncidentListFilters = {}) {
  return useQuery<IncidentListResult>({
    queryKey: hcKeys.incidents(filters as Record<string, unknown>),
    staleTime: 15_000,
    refetchInterval: 30_000,
    queryFn: async () =>
      unwrapList<AlarmEvent>(await healthcheckApi.get('/alarm-events', { params: filters })),
  });
}

export function useAlarmEventCount() {
  return useQuery<AlarmEventCount>({
    queryKey: hcKeys.incidentCount(),
    refetchInterval: 30_000,
    queryFn: async () =>
      unwrap<AlarmEventCount>(await healthcheckApi.get('/alarm-events/count')),
  });
}

function useInvalidateIncidents() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ['hc', 'incidents'] });
}

export function useAcknowledgeIncident() {
  const invalidate = useInvalidateIncidents();
  // BE reads `acknowledged_by` from body, defaults to "system" if absent.
  const { user } = useAuth();
  const actor = user?.name ?? user?.email ?? 'system';
  return useMutation<void, Error, string>({
    mutationFn: async (id) => {
      await healthcheckApi.post(`/alarm-events/${id}/acknowledge`, { acknowledged_by: actor });
    },
    onSuccess: invalidate,
  });
}

export function useResolveIncident() {
  const invalidate = useInvalidateIncidents();
  // BE reads `resolved_by` from body, defaults to "system" if absent.
  const { user } = useAuth();
  const actor = user?.name ?? user?.email ?? 'system';
  return useMutation<void, Error, string>({
    mutationFn: async (id) => {
      await healthcheckApi.post(`/alarm-events/${id}/resolve`, { resolved_by: actor });
    },
    onSuccess: invalidate,
  });
}
